import { Component, signal, inject, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AssetsService } from '../../services/asset.service';
import { SpinnerComponent } from '../../../public-api';
import { StorageService } from '../../../public-api';

import { UiService } from '../../../public-api';
import { I18nService } from '../../../public-api';
import { ICountry } from '../../models/object.model';
import { CommonFormComponent, FormField } from '../../components/common-form/common-form';
import { CustomValidators } from '../../utilities/customer_validators';
import { AuthenticationService } from '../../../public-api';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { FormsModule } from '@angular/forms';
import { FormKey, I18N_KEYS } from '../../enums/i18.enum';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { logError } from '../../../public-api';
@Component({
  selector: 'app-signup',
  imports: [
    CommonModule,
    RouterModule,
    NzImageModule,
    NzButtonModule,
    NzIconModule,
    NzSpinModule,
    SpinnerComponent,
    NzRadioModule,
    CommonFormComponent,
    FormsModule,
  ],
  templateUrl: './admin-approval.html',
  styleUrls: ['./admin-approval.scss'],
})
export class AdminApproval implements OnInit {
  // Inject services using inject()
  private router = inject(Router);
  public assetService = inject(AssetsService);
  public ui = inject(UiService);
  public i18n = inject(I18nService);
  public route = inject(ActivatedRoute);
  public storage = inject(StorageService);
  private authService = inject(AuthenticationService);

  // Use signals for state management
  isLoading = signal(false);
  error = signal(false);
  optional = signal(false);
  countryList = signal<ICountry[]>([]);

  isMobile = this.ui.IsMobile;
  isTablet = this.ui.IsTablet;
  isDesktop = this.ui.IsDesktop;
  userHasAccessTo = [
    {
      id: 0,
      name: this.i18n.langString(I18N_KEYS.EM_IA_BOTH),
    },
    {
      id: 1,
      name: this.i18n.langString(I18N_KEYS.EM_ONLY),
    },
  ];
  isDenied = false;
  isGranted = false;
  countryArray = signal<ICountry[]>([]);
  customerList = [];
  token: any;
  sourceCountries: any[] = [];
  country: any;
  filteredCountries: any;

  adminAccessFormConfig: FormField[] = [
    {
      controlName: FormKey.Name,
      label: this.i18n.langString(FormKey.Name),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.Name),
      initialValue: '',
      validators: [...CustomValidators.requiredMinLength(3)],
    },
    {
      controlName: FormKey.Surname,
      label: this.i18n.langString(FormKey.Surname),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.Surname),
      initialValue: '',
      validators: [...CustomValidators.requiredMinLength(3)],
    },
    {
      controlName: FormKey.Email,
      label: this.i18n.langString(FormKey.Email),
      controlType: FormKey.Text,
      placeholder: FormKey.EmailPlaceholder,
      initialValue: '',
      validators: [...CustomValidators.email],
    },
    {
      controlName: FormKey.Title,
      label: this.i18n.langString(FormKey.Designation),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.Designation),
      initialValue: '',
      validators: [Validators.required],
    },
    {
      controlName: FormKey.Country,
      label: this.i18n.langString(FormKey.Country),
      controlType: FormKey.Select,
      placeholder: this.i18n.langString(FormKey.SelectCountry),
      initialValue: '',
      validators: [Validators.required],
    },
    {
      controlName: FormKey.CustomerEntity,
      label: this.i18n.langString(FormKey.CustomerEntity),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.CustomerEntity),
      initialValue: '',
      validators: [Validators.required],
    },
    {
      controlName: FormKey.Customer,
      label: this.i18n.langString(FormKey.SelectCustomer),
      controlType: FormKey.Select,
      placeholder: this.i18n.langString(FormKey.SelectCustomer),
      initialValue: '',
      validators: [Validators.required],
    },
    {
      controlName: FormKey.Access,
      label: this.i18n.langString(FormKey.UserHasAccessTo),
      controlType: FormKey.Radio,
      placeholder: GLOBAL_CONSTANT.EMPTY_STRING,
      initialValue: null,
      validators: [Validators.required],
      options: [...this.userHasAccessTo],
    },
  ];

  @ViewChild(CommonFormComponent)
  adminForm!: CommonFormComponent;

  ngOnInit() {
    this.assetService.getCountryList().then((countryList) => {
      this.sourceCountries = countryList;
      const isUSSelected = this.country === 'us';
      this.filteredCountries = this.sourceCountries.filter((item: { name: any }) => {
        try {
          const parsed = JSON.parse(item.name.trim());
          const countryName = parsed.en;
          if (isUSSelected) {
            // US selected → show only US and Canada
            return countryName === 'United States' || countryName === 'Canada';
          } else {
            // Not US → exclude US and Canada
            return countryName !== 'United States' && countryName !== 'Canada';
          }
        } catch (e) {
          console.error(e);
          return false;
        }
      });
      this.countryArray.set(this.filteredCountries);
    });
    this.route.params.subscribe((params) => {
      if (params['token']) {
        this.token = params['token'];
        this.loadUserData();
      }
    });
  }

  loadUserData() {
    this.authService.loadUserApprovalData(this.token).subscribe((result) => {
      const user = result.userData;
      this.customerList = result.customerList;
      setTimeout(() => {
        this.adminForm.form.patchValue({
          name: user.name,
          surname: user.surname,
          email: user.email,
          title: user.designation ?? '',
          country: user.country != null ? Number(user.country) : null,
          customerentity: user.customerEntity ?? '',
        });
      });
    });
  }

  sortCountries() {
    this.countryArray.set(
      this.countryArray().sort((a: { name: any }, b: { name: any }) => {
        if (this.i18n.parse(a.name).toUpperCase() > this.i18n.parse(b.name).toUpperCase()) return 1;
        if (this.i18n.parse(a.name).toUpperCase() < this.i18n.parse(b.name).toUpperCase())
          return -1;
        return 0;
      })
    );
  }

  approve(formData: any) {
    this.error.set(false);
    this.isLoading.set(true);
    const isAccessToIA = formData.access == '0' ? true : false;
    this.authService
      .processRegistrationRequest(
        true,
        formData.customer,
        this.token,
        isAccessToIA,
        GLOBAL_CONSTANT.EMPTY_STRING
      )
      .subscribe(
        (res) => {
          console.log(res);
          this.isLoading.set(false);
          this.isGranted = true;
        },
        (error) => {
          logError(error);
          this.isLoading.set(false);
          this.error.set(true);
        }
      );
  }

  deny(denialReason: string) {
    this.error.set(false);
    this.isLoading.set(true);
    this.authService
      .processRegistrationRequest(false, -1, this.token, false, denialReason)
      .subscribe(
        (_res) => {
          this.isLoading.set(false);
          this.isDenied = true;
        },
        (error) => {
          this.isLoading.set(false);
          this.error.set(true);
          logError(error);
        }
      );
  }
}
